import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';
import ConfirmDialog from '../common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const SHOW_TYPES = [
  { value: 'radio', label: 'Radio show (música + jingles)' },
  { value: 'dj', label: 'DJ (sets)' },
];

const SEPARATOR_INTERVAL_OPTIONS = [2, 3, 4, 5, 6, 10];
const SEPARATOR_SONGS_OPTIONS = [2, 3, 4, 5, 6];

function JingleSelect({ label, value, onChange, jingles }) {
  return (
    <div>
      <label className="block text-sm font-medium text-dark-300 mb-1">{label}</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} className="input text-sm">
        <option value="">— Sin archivo —</option>
        {jingles.map((j) => (
          <option key={j.id} value={j.id}>
            {j.metadata?.title || j.original_filename}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function ShowForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const isDj = user?.role === 'dj';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dj_id: '',
    duration_minutes: '',
    show_type: 'radio',
    is_active: true,
    image_url: '',
    color: '#7c3aed',
    intro_media_file_id: null,
    closing_media_file_id: null,
    separator_media_file_ids: [],
    separator_interval_minutes: 15,
    separator_interval_songs: 3,
    set_folder_id: '',
    refill_folder_id: '',
    use_transition_fx: false,
  });

  const [djs, setDjs] = useState([]);
  const [jingles, setJingles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [showFolders, setShowFolders] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isDj) {
      fetchDjs();
      fetchJingles();
      fetchFolders();
    }
    if (isEdit) {
      fetchShow();
    }
  }, [id]);

  const fetchDjs = async () => {
    try {
      const { data } = await api.get('/api/djs', { params: { role: 'dj' } });
      setDjs(data.djs || data || []);
    } catch (err) {
      console.error('[ShowForm] Failed to fetch DJs:', err);
    }
  };

  const fetchJingles = async () => {
    try {
      const params = { category: 'jingle', processing_status: 'ready', limit: 200 };
      const { data } = await api.get('/api/media', { params });
      setJingles(data.files || []);
    } catch (err) {
      console.error('[ShowForm] Failed to fetch jingles:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      const { data } = await api.get('/api/music/folders');
      setFolders(data || []);
    } catch (err) {
      console.error('[ShowForm] Failed to fetch folders:', err);
    }
  };

  const fetchShow = async () => {
    try {
      const { data } = await api.get(`/api/shows/${id}`);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        dj_id: data.dj_id || '',
        duration_minutes: data.duration_minutes || '',
        show_type: data.show_type || 'radio',
        is_active: data.is_active ?? true,
        image_url: data.image_url || '',
        color: data.color || '#7c3aed',
        intro_media_file_id: data.intro_media_file_id || null,
        closing_media_file_id: data.closing_media_file_id || null,
        separator_media_file_ids: data.separator_media_file_ids || [],
        separator_interval_minutes: data.separator_interval_minutes || 15,
        separator_interval_songs: data.separator_interval_songs || 3,
        set_folder_id: data.set_folder_id || '',
        refill_folder_id: data.refill_folder_id || '',
        use_transition_fx: data.use_transition_fx ?? false,
      });
      setShowFolders(data.folders || []);
      fetchJingles();
    } catch (err) {
      console.error('[ShowForm] Failed to fetch show:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleSeparator = (fileId) => {
    setFormData((prev) => {
      const current = prev.separator_media_file_ids || [];
      const next = current.includes(fileId)
        ? current.filter((f) => f !== fileId)
        : [...current, fileId];
      return { ...prev, separator_media_file_ids: next };
    });
  };

  const addFolderToShow = async (folderId) => {
    if (!isEdit || !folderId) return;
    try {
      const { data } = await api.post(`/api/shows/${id}/folders`, { folder_id: folderId, weight: 100 });
      const updated = [...showFolders, data];
      setShowFolders(updated);
      fetchJingles(updated.map((f) => f.folder_id));
    } catch (err) {
      console.error('[ShowForm] Add folder failed:', err);
    }
  };

  const removeFolderFromShow = async (rowId) => {
    if (!isEdit) return;
    try {
      await api.delete(`/api/shows/${id}/folders/${rowId}`);
      const updated = showFolders.filter((f) => f.id !== rowId);
      setShowFolders(updated);
      fetchJingles(updated.map((f) => f.folder_id));
    } catch (err) {
      console.error('[ShowForm] Remove folder failed:', err);
    }
  };

  const updateFolderWeight = async (rowId, weight) => {
    if (!isEdit) return;
    try {
      await api.put(`/api/shows/${id}/folders/${rowId}`, { weight: parseInt(weight, 10) || 0 });
      setShowFolders(showFolders.map((f) => (f.id === rowId ? { ...f, weight } : f)));
    } catch (err) {
      console.error('[ShowForm] Update folder weight failed:', err);
    }
  };

  const handleShowImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 400;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        setFormData((prev) => ({ ...prev, image_url: canvas.toDataURL('image/jpeg', 0.7) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre de la serie es obligatorio';
    if (!formData.show_type) newErrors.show_type = 'El tipo de serie es obligatorio';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes, 10) : 60,
        dj_id: formData.dj_id || null,
        set_folder_id: formData.set_folder_id || null,
        refill_folder_id: formData.refill_folder_id || null,
        intro_media_file_id: formData.intro_media_file_id || null,
        closing_media_file_id: formData.closing_media_file_id || null,
        separator_media_file_ids: formData.separator_media_file_ids || [],
        separator_interval_minutes: parseInt(formData.separator_interval_minutes, 10) || 15,
        separator_interval_songs: parseInt(formData.separator_interval_songs, 10) || 3,
        use_transition_fx: Boolean(formData.use_transition_fx),
      };

      if (isEdit) {
        await api.put(`/api/shows/${id}`, payload);
      } else {
        await api.post('/api/shows', payload);
      }
      navigate('/shows');
    } catch (err) {
      console.error('[ShowForm] Save failed:', err);
      setErrors({ submit: err.response?.data?.message || 'Error al guardar la serie.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/shows/${id}`);
      navigate('/shows');
    } catch (err) {
      console.error('[ShowForm] Delete failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando serie...</p>
      </div>
    );
  }

  // Vista DJ: solo ve su serie y sube su set
  if (isDj) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-dark-100">{formData.name}</h1>
          <Link to="/shows" className="btn btn-secondary btn-sm">Volver a Mis Series</Link>
        </div>

        <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
          <p className="text-sm text-dark-300 mb-4">
            {formData.description || 'Sube tu set (mix de 1-2 horas) para tu programa. El archivo se procesará y se emitirá en tu horario.'}
          </p>
          <div className="flex items-center gap-4">
            <Link to={`/shows/${id}/episodes/upload`} className="btn btn-primary">
              🎧 Subir mi set
            </Link>
            <Link to={`/shows/${id}/episodes`} className="btn btn-secondary">
              Ver mis episodios
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Editar Serie' : 'Crear Serie'}
        </h1>
        <Link to="/shows" className="btn btn-secondary btn-sm">
          Volver a Series
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Imagen */}
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-dark-850 border border-dark-700 p-6">
            <div className="relative h-24 w-24 rounded-lg border-2 border-dashed border-dark-700 bg-dark-800 flex items-center justify-center overflow-hidden">
              {formData.image_url ? (
                <img src={formData.image_url} alt="preview" className="h-full w-full object-cover" />
              ) : (
                <span className="text-3xl text-dark-500">📻</span>
              )}
            </div>
            <span className="text-xs text-dark-500">Imagen de la serie</span>
            <label className="cursor-pointer">
              <span className="btn btn-secondary btn-sm">
                {formData.image_url ? 'Cambiar foto' : 'Subir foto'}
              </span>
              <input type="file" accept="image/*" onChange={handleShowImage} className="hidden" />
            </label>
            {formData.image_url && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, image_url: '' }))}
                className="text-xs text-error-400 hover:text-error-300"
              >
                Quitar
              </button>
            )}
            <div className="flex items-center gap-2">
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="h-8 w-8 cursor-pointer rounded border border-dark-700 bg-transparent p-0"
              />
              <span className="text-xs text-dark-400">{formData.color}</span>
            </div>
          </div>

          {isEdit && (
            <Link to={`/shows/${id}/schedule`} className="btn btn-primary w-full">
              🗓️ Programación semanal
            </Link>
          )}
        </div>

        {/* Formulario principal */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-dark-100 mb-2">Información básica</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Nombre *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`input ${errors.name ? 'border-error-500' : ''}`}
                  placeholder="Ej: La Hora del Techno"
                />
                {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Tipo de serie *</label>
                <select
                  name="show_type"
                  value={formData.show_type}
                  onChange={handleChange}
                  className={`input ${errors.show_type ? 'border-error-500' : ''}`}
                >
                  {SHOW_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {formData.show_type === 'dj' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-1">DJ asignado</label>
                    <select name="dj_id" value={formData.dj_id} onChange={handleChange} className="input">
                      <option value="">Sin DJ (música automática)</option>
                      {djs.map((dj) => (
                        <option key={dj.id} value={dj.id}>{dj.name || dj.stage_name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-dark-500 mt-1">Si asignás un DJ, él verá esta serie para subir su set.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-1">Carpeta del set (DJ)</label>
                    <select name="set_folder_id" value={formData.set_folder_id} onChange={handleChange} className="input">
                      <option value="">— Elegir carpeta del set —</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-dark-500 mt-1">La carpeta donde el DJ sube su set semanal (1 archivo).</p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Duración (minutos)</label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ej: 60"
                  min="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="input min-h-[80px]"
                placeholder="Descripción de la serie..."
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-dark-300">Activa</label>
</div>
        </div>

          {/* Identificación/jingle para series tipo DJ */}
          {formData.show_type === 'dj' && (
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-dark-100">Identificación de la radio</h3>
            <p className="text-xs text-dark-500">
              Configurá una identificación o jingle que suene al comienzo del set del DJ (y opcionalmente al cierre).
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <JingleSelect
                label="Identificación / jingle al INICIO"
                value={formData.intro_media_file_id}
                onChange={(v) => setFormData((p) => ({ ...p, intro_media_file_id: v }))}
                jingles={jingles}
              />
              <JingleSelect
                label="Identificación / jingle al FINAL"
                value={formData.closing_media_file_id}
                onChange={(v) => setFormData((p) => ({ ...p, closing_media_file_id: v }))}
                jingles={jingles}
              />
            </div>
          </div>
          )}

          {/* Esquema radio show (solo series de tipo radio) */}
          {formData.show_type === 'radio' && (
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-dark-100">Esquema radio show</h3>
            <p className="text-xs text-dark-500">
              Para series de tipo radio show: la hora se arma como Intro → música → separadores → música → … → Cierre.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <JingleSelect
                label="Intro de la hora"
                value={formData.intro_media_file_id}
                onChange={(v) => setFormData((p) => ({ ...p, intro_media_file_id: v }))}
                jingles={jingles}
              />
              <JingleSelect
                label="Cierre de la hora"
                value={formData.closing_media_file_id}
                onChange={(v) => setFormData((p) => ({ ...p, closing_media_file_id: v }))}
                jingles={jingles}
              />
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-1">Separador cada</label>
                <select
                  name="separator_interval_songs"
                  value={formData.separator_interval_songs}
                  onChange={handleChange}
                  className="input text-sm"
                >
                  {SEPARATOR_SONGS_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} canciones</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">
                Separadores disponibles ({formData.separator_media_file_ids.length} seleccionados)
              </label>
              {jingles.length === 0 ? (
                <p className="text-sm text-dark-500">
                  No hay jingles. Subí archivos de audio con categoría "Jingle" en la Biblioteca.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {jingles.map((j) => {
                    const selected = formData.separator_media_file_ids.includes(j.id);
                    return (
                      <button
                        key={j.id}
                        type="button"
                        onClick={() => toggleSeparator(j.id)}
                        className={`rounded-md px-3 py-1 text-xs border transition-colors ${
                          selected
                            ? 'bg-primary-900/40 border-primary-600 text-primary-300'
                            : 'bg-dark-800 border-dark-700 text-dark-400 hover:border-dark-500'
                        }`}
                      >
                        {j.metadata?.title || j.original_filename}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-dark-800 border border-dark-700 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-dark-200">Efectos de transición (FX / sweep)</p>
                <p className="text-xs text-dark-500">
                  Superpone un efecto al azar de la carpeta FX durante el crossfade entre canciones de esta serie.
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={formData.use_transition_fx}
                  onChange={(e) => setFormData((p) => ({ ...p, use_transition_fx: e.target.checked }))}
                />
                <div className="h-6 w-11 rounded-full bg-dark-600 peer-checked:bg-primary-600 transition-colors" />
                <span className="ml-2 text-sm text-dark-300">{formData.use_transition_fx ? 'Activado' : 'Desactivado'}</span>
              </label>
            </div>
          </div>
          )}

          {/* Carpetas de música */}
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-dark-100">Carpetas de música</h3>
            <p className="text-xs text-dark-500">
              Elegí 1 o más carpetas de las que la serie sacará música al azar. El peso define la proporción (100 = equitativo).
            </p>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">
                Carpeta de autocompletado "próximos"
              </label>
              <select
                name="refill_folder_id"
                value={formData.refill_folder_id}
                onChange={handleChange}
                className="input"
              >
                <option value="">— Automático (música general) —</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <p className="text-xs text-dark-500 mt-1">
                De qué carpeta se rellenan los "próximos" mientras corre este programa. En DJ (sets) evita repetir el set; en radio show, si se elige, se usa esta carpeta en vez de las de arriba.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Agregar carpeta</label>
              <div className="flex gap-2">
                <select id="folder-picker" className="input flex-1" defaultValue="">
                  <option value="" disabled>Seleccionar carpeta de música...</option>
                  {folders
                    .filter((f) => !showFolders.some((sf) => sf.folder_id === f.id))
                    .map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const sel = document.getElementById('folder-picker').value;
                    if (sel) addFolderToShow(sel);
                    document.getElementById('folder-picker').value = '';
                  }}
                  disabled={!isEdit}
                >
                  + Agregar
                </button>
              </div>
              {!isEdit && (
                <p className="text-xs text-warning-400 mt-1">Guardá la serie primero para asignar carpetas.</p>
              )}
            </div>

            {showFolders.length === 0 ? (
              <p className="text-sm text-dark-500">Sin carpetas asignadas.</p>
            ) : (
              <div className="space-y-2">
                {showFolders.map((row) => (
                  <div key={row.id} className="flex items-center gap-3 rounded-lg bg-dark-800/50 px-3 py-2">
                    <span className="text-lg">📁</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-100 truncate">{row.folder_name}</p>
                    </div>
                    <input
                      type="number"
                      min="1"
                      value={row.weight}
                      onChange={(e) => updateFolderWeight(row.id, e.target.value)}
                      className="input text-xs w-20 text-center"
                      title="Peso (%)"
                    />
                    <button
                      type="button"
                      onClick={() => removeFolderFromShow(row.id)}
                      className="text-xs text-error-400 hover:text-error-300"
                      title="Quitar carpeta"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </form>

      <div className="flex items-center justify-between border-t border-dark-700 pt-6">
        <div>
          {isEdit && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="btn btn-error btn-sm"
            >
              Eliminar Serie
            </button>
          )}
        </div>
        <div className="flex gap-3">
          <Link to="/shows" className="btn btn-secondary">Cancelar</Link>
          <button type="submit" disabled={saving} onClick={handleSubmit} className="btn btn-primary">
            {saving ? 'Guardando...' : isEdit ? 'Actualizar Serie' : 'Crear Serie'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Serie"
        message={`¿Seguro que quieres eliminar "${formData.name}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
